import { useState, useEffect } from 'react';

/**
 * Hook that listens to a CSS media query and returns whether it matches
 *
 * @param query - CSS media query string (e.g., '(min-width: 800px)')
 * @returns boolean indicating whether the media query matches
 *
 * @example
 * ```tsx
 * const isLargeScreen = useMediaQuery('(min-width: 1024px)');
 * ```
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    // Check if window is defined (for SSR compatibility)
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(query);

    // Set initial value
    setMatches(mediaQuery.matches);

    // Create event listener
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Add listener (modern API)
    mediaQuery.addEventListener('change', handleChange);

    // Cleanup
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
}

/**
 * Hook that returns true when viewport is in tablet range (800px - 1199px)
 *
 * @returns boolean indicating tablet viewport
 *
 * @example
 * ```tsx
 * const isTablet = useIsTablet();
 * if (isTablet) {
 *   // Apply tablet-specific layout
 * }
 * ```
 */
export function useIsTablet(): boolean {
  return useMediaQuery('(min-width: 800px) and (max-width: 1199px)');
}

/**
 * Hook that returns true when viewport is desktop size (>= 1200px)
 *
 * @returns boolean indicating desktop viewport
 */
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 1200px)');
}

/**
 * Hook that returns true when viewport is mobile size (< 800px)
 *
 * @returns boolean indicating mobile viewport
 */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 799px)');
}

/**
 * Hook that returns true when viewport is narrow mobile size (< 450px)
 * Used for layouts that need to adapt when action buttons can't fit in a single row
 *
 * @returns boolean indicating narrow mobile viewport
 */
export function useIsNarrowMobile(): boolean {
  return useMediaQuery('(max-width: 449px)');
}
